import moment from 'moment';
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react';

export const DEFAULT_TIMEOUT_MINUTES = 1;
// Mapping of impression event key to last impression timestamp ISO string
export type FiredImpressionMap = Partial<Record<string, string>>;

export interface ImpressionContextType {
  fireDedupedImpressionEvent: (
    impressionEventKey: string,
    fireImpressionEvent: () => void,
    timeoutMinutes: number,
  ) => void;
  firedImpressionsMap: FiredImpressionMap;
}

interface Props {
  children: React.ReactNode;
}

export const ImpressionContextProvider: React.FC<Props> = ({ children }) => {
  const firedImpressionsMapRef = useRef<FiredImpressionMap>({});

  const fireDedupedImpressionEvent = useCallback(
    (
      impressionEventKey: string,
      fireImpressionEvent: () => void,
      cutoffMinutes: number,
    ) => {
      const firedImpressionsMap = firedImpressionsMapRef.current;
      const previousImpressionISO = firedImpressionsMap[impressionEventKey];
      const now = moment();

      // Fire impression if not previously fired or past cutoff time from last event
      if (
        !previousImpressionISO ||
        moment(previousImpressionISO).add(cutoffMinutes, 'minute').isBefore(now)
      ) {
        fireImpressionEvent();
      }

      firedImpressionsMap[impressionEventKey] = now.toISOString();
    },
    [],
  );

  const value = useMemo(
    () => ({
      fireDedupedImpressionEvent,
      firedImpressionsMap: firedImpressionsMapRef.current,
    }),
    [fireDedupedImpressionEvent],
  );

  return (
    <ImpressionContext.Provider value={value}>
      {children}
    </ImpressionContext.Provider>
  );
};

export const ImpressionContext = createContext<ImpressionContextType | null>(
  null,
);

export const useImpressionContext = () => {
  const context = useContext(ImpressionContext);
  if (!context) {
    throw new Error(
      'useImpressionContext requires a ImpressionContextProvider',
    );
  }

  return context;
};
